import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Button, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tabs, Tab, Snackbar,
} from '@mui/material';
import {
  AdminPanelSettings, Refresh, PersonAdd, Block, CheckCircle, LockReset, Delete,
  Circle, ContentCopy, Visibility,
} from '@mui/icons-material';
import apiService from '../services/api';
import { AdminOverview, AdminUserUsage } from './AdminUsage';

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function fmt(iso: string | null): string { return iso ? new Date(iso).toLocaleString() : '—'; }

// Friendly names for SPA routes (page views) and common API endpoints.
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard', '/scoring': 'Stock Scoring', '/ranges': 'Volatility Ranges',
  '/predictions': 'ML Predictions', '/signals': 'Trading Signals', '/patterns': 'Pattern Matching',
  '/position': 'Market Position', '/movers': 'Top Movers', '/recommendations': 'Recommendations',
  '/trading': 'Trading', '/backtest': 'Backtest', '/daily-tasks': 'Daily Tasks',
  '/news-signals': 'News Signals', '/media-trending': 'Media Trending', '/future-stocks': 'Stocks of Future',
  '/israeli-startups': 'Israeli Startups', '/israeli-news': 'Israeli News', '/admin': 'Admin Console',
  '/change-password': 'Change Password',
};

function describeActivity(a: any): string {
  if (a.method === 'PAGE') return ROUTE_LABELS[a.path] || `Page ${a.path}`;
  // Shorten the API path for readability
  return (a.path || '').replace('/api/v1', '');
}

export default function Admin() {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [activityUser, setActivityUser] = useState<number | null>(null);
  const [usageUser, setUsageUser] = useState<number | null>(null);

  // create-user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [tempResult, setTempResult] = useState<{ username: string; password: string } | null>(null);

  // The public tunnel intermittently 502s on bursts of parallel requests, so
  // load sequentially and retry once on a transient gateway error.
  const withRetry = async <T,>(fn: () => Promise<T>): Promise<T> => {
    try { return await fn(); }
    catch (e: any) {
      const code = e?.response?.status;
      if (code === 502 || code === 503 || code === 504 || !code) {
        await new Promise(r => setTimeout(r, 600));
        return await fn();
      }
      throw e;
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const u = await withRetry(() => apiService.adminListUsers());
      setUsers(u.users ?? []);
      const s = await withRetry(() => apiService.adminSessions());
      setSessions(s.sessions ?? []);
      const a = await withRetry(() => apiService.adminActivity({ limit: 150 }));
      setActivity(a.activity ?? []);
      setError(null);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to load admin data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadActivityFor = async (userId: number | null) => {
    setActivityUser(userId);
    const a = await apiService.adminActivity(userId ? { user_id: userId, limit: 300 } : { limit: 300 });
    setActivity(a.activity ?? []);
    setTab(2);
  };

  const copy = (txt: string, label: string) => navigator.clipboard.writeText(txt).then(() => setToast(`${label} copied`));

  const doCreate = async () => {
    try {
      const res = await apiService.adminCreateUser(newUsername.trim(), newEmail.trim());
      setTempResult({ username: newUsername.trim(), password: res.temporary_password });
      setNewUsername(''); setNewEmail('');
      await loadAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create user');
    }
  };

  const patch = async (id: number, body: any, label: string) => {
    try {
      const res = await apiService.adminUpdateUser(id, body);
      if (res.temporary_password) setTempResult({ username: res.user.username, password: res.temporary_password });
      await loadAll();
      setToast(label);
    } catch (e: any) { setError(e.response?.data?.detail || 'Action failed'); }
  };

  const del = async (id: number, username: string) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try { await apiService.adminDeleteUser(id); await loadAll(); setToast('User deleted'); }
    catch (e: any) { setError(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AdminPanelSettings color="primary" sx={{ fontSize: 30 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Admin Console</Typography>
            <Typography variant="caption" color="text.secondary">
              Manage users, monitor active sessions, and audit activity.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<PersonAdd />} onClick={() => { setTempResult(null); setCreateOpen(true); }}>New user</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadAll} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label={`Users (${users.length})`} />
        <Tab label={`Active sessions (${sessions.length})`} />
        <Tab label="Usage" />
        <Tab label="Activity log" />
      </Tabs>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box> : (
        <>
          {tab === 0 && <AdminOverview onSelectUser={id => { setUsageUser(id); setTab(3); }} />}
          {tab === 3 && (
            <AdminUserUsage userId={usageUser ?? (users[0]?.id ?? 0)} users={users} onChangeUser={id => setUsageUser(id)} />
          )}

          {/* USERS */}
          {tab === 1 && (
            <Card><CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead><TableRow>
                  {['User', 'Email', 'Status', 'MFA', 'Last login', 'Last seen', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Circle sx={{ fontSize: 10, color: u.online ? 'success.main' : 'text.disabled' }} />
                          <Box component="span" sx={{ fontWeight: 600 }}>{u.username}</Box>
                          {u.is_admin && <Chip size="small" label="admin" color="primary" sx={{ height: 17, fontSize: '0.6rem' }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{u.email}</TableCell>
                      <TableCell>
                        {u.is_active
                          ? <Chip size="small" label="active" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                          : <Chip size="small" label="disabled" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
                        {u.must_change_password && <Chip size="small" label="temp pw" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.62rem', ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{u.mfa_enabled ? <CheckCircle color="success" sx={{ fontSize: 18 }} /> : <Block color="disabled" sx={{ fontSize: 18 }} />}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{fmt(u.last_login)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{timeAgo(u.last_seen)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                          <Tooltip title="View activity"><IconButton size="small" onClick={() => loadActivityFor(u.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={u.is_active ? 'Disable account' : 'Enable account'}>
                            <IconButton size="small" onClick={() => patch(u.id, { is_active: !u.is_active }, u.is_active ? 'Account disabled' : 'Account enabled')}>
                              {u.is_active ? <Block fontSize="small" color="error" /> : <CheckCircle fontSize="small" color="success" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset password (issue temp)"><IconButton size="small" onClick={() => patch(u.id, { reset_password: true }, 'Password reset')}><LockReset fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete user"><IconButton size="small" onClick={() => del(u.id, u.username)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}

          {/* SESSIONS */}
          {tab === 2 && (
            <Card><CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Users active within the last 15 minutes. {sessions.length === 0 && 'No active sessions right now.'}
              </Typography>
              {sessions.map(s => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Circle sx={{ fontSize: 11, color: 'success.main' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.username} {s.is_admin && <Chip size="small" label="admin" color="primary" sx={{ height: 16, fontSize: '0.58rem' }} />}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="success.main">active · {timeAgo(s.last_seen)}</Typography>
                    <Button size="small" sx={{ ml: 1 }} onClick={() => loadActivityFor(s.id)}>activity</Button>
                  </Box>
                </Box>
              ))}
            </CardContent></Card>
          )}

          {/* ACTIVITY */}
          {tab === 4 && (
            <Card><CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {activityUser ? `Activity for user #${activityUser}` : 'All recent activity'} — {activity.length} events
                </Typography>
                {activityUser && <Button size="small" onClick={() => loadActivityFor(null)}>Show all</Button>}
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  {['When', 'User', 'Action', 'Page / Endpoint', 'Ticker', 'IP'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {activity.map(a => {
                    const isPage = a.method === 'PAGE';
                    return (
                      <TableRow key={a.id} hover>
                        <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{timeAgo(a.created_at)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{a.username || `#${a.user_id}`}</TableCell>
                        <TableCell>
                          <Chip size="small" label={isPage ? 'Page view' : a.method}
                            color={isPage ? 'secondary' : 'default'}
                            sx={{ height: 17, fontSize: '0.6rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: isPage ? '0.78rem' : '0.72rem',
                          fontFamily: isPage ? 'inherit' : 'monospace',
                          fontWeight: isPage ? 600 : 400,
                          color: isPage ? 'text.primary' : 'text.secondary' }}>
                          {describeActivity(a)}
                        </TableCell>
                        <TableCell>{a.ticker ? <Chip size="small" label={a.ticker} color="info" sx={{ height: 18, fontSize: '0.62rem' }} /> : '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{a.ip}</TableCell>
                      </TableRow>
                    );
                  })}
                  {activity.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.disabled' }}>No activity recorded yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </>
      )}

      {/* Create-user dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create new user</DialogTitle>
        <DialogContent>
          {tempResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                User <strong>{tempResult.username}</strong> created. Share this temporary password securely — it won't be shown again.
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 'bold', flex: 1 }}>{tempResult.password}</Typography>
                <IconButton size="small" onClick={() => copy(tempResult.password, 'Temp password')}><ContentCopy fontSize="small" /></IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                On first login the user sets up Google Authenticator (TOTP), then is required to choose their own password.
              </Typography>
            </Box>
          ) : (
            <>
              <TextField label="Username" fullWidth margin="normal" value={newUsername} onChange={e => setNewUsername(e.target.value)} autoFocus />
              <TextField label="Email" fullWidth margin="normal" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {tempResult ? (
            <Button onClick={() => { setCreateOpen(false); setTempResult(null); }}>Done</Button>
          ) : (
            <>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={!newUsername.trim() || !newEmail.trim()} onClick={doCreate}>Create</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
}
